import { Heart } from "lucide-react";
import { Reveal } from "./Reveal";

const PATIENT_POINTS = [
  "Capture the day as it happens.",
  "Keep meaningful experiences visible between visits.",
  "Reduce the pressure of remembering weeks or months of information during one appointment.",
  "Bring clearer context into conversations with a care team.",
];

const TEAM_POINTS = [
  "Review clearer patient context.",
  "Understand what happened between visits.",
  "Notice meaningful change more easily.",
  "Spend less time digging through scattered information.",
  "Support better continuity and more informed conversations.",
];

function Point({ children }) {
  return (
    <li className="flex items-start gap-3">
      <Heart
        aria-hidden="true"
        className="mt-1.5 h-4 w-4 shrink-0 fill-brand-coral text-brand-coral"
      />
      <span className="text-[17px] font-medium leading-[1.55] md:text-[19px]">
        {children}
      </span>
    </li>
  );
}

export default function ForEveryone() {
  return (
    <section
      id="for-everyone"
      className="relative scroll-mt-24 lg:scroll-mt-28 overflow-hidden py-20 md:py-28"
      data-testid="for-everyone-section"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -right-24 top-[8%] h-[220px] w-[220px] rounded-full bg-brand-aqua/40" />
        <div className="absolute -left-20 top-[42%] h-[180px] w-[180px] rounded-full bg-brand-peach/50" />
        <div className="absolute right-[14%] top-[36%] h-[120px] w-[120px] rounded-full border-2 border-brand-coral/15 border-l-transparent border-t-transparent" />
      </div>
      <div className="symp-container relative">
        <Reveal>
          <div className="flex items-start gap-3">
            <h2 className="section-title text-brand-teal">For Everyone</h2>
            <Heart
              aria-hidden="true"
              className="mt-2 h-5 w-5 shrink-0 fill-brand-coral text-brand-coral md:mt-3 md:h-6 md:w-6"
            />
          </div>
          <p className="mt-4 max-w-[720px] text-[18px] font-medium leading-[1.6] text-brand-ink md:text-[20px]">
            SympTrack brings patients and care teams closer around one shared
            view of the space between visits.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-8 lg:grid-cols-2 lg:gap-12">
          <Reveal delay={70}>
            <article
              id="for-patients"
              className="scroll-mt-28 rounded-[40px] rounded-tr-[88px] bg-brand-aqua/45 p-8 md:p-12"
              data-testid="for-patients-panel"
            >
              <h3 className="text-[26px] font-extrabold text-brand-teal md:text-[30px]">
                For patients
              </h3>
              <p className="mt-3 text-[17px] font-semibold leading-[1.55] text-brand-ink md:text-[19px]">
                Your days hold more than any appointment can hold. SympTrack
                helps you keep what happened in view.
              </p>
              <ul className="mt-6 space-y-4">
                {PATIENT_POINTS.map((point) => (
                  <Point key={point}>{point}</Point>
                ))}
              </ul>
            </article>
          </Reveal>
          <Reveal delay={140}>
            <article
              id="for-providers"
              className="scroll-mt-28 rounded-[40px] rounded-bl-[88px] bg-brand-peach/45 p-8 md:p-12 lg:mt-14"
              data-testid="for-providers-panel"
            >
              <h3 className="text-[26px] font-extrabold text-brand-teal md:text-[30px]">
                For care teams
              </h3>
              <p className="mt-3 text-[17px] font-semibold leading-[1.55] text-brand-ink md:text-[19px]">
                Context changes conversations. SympTrack organizes what
                happened between visits so it is ready when you sit down
                together.
              </p>
              <ul className="mt-6 space-y-4">
                {TEAM_POINTS.map((point) => (
                  <Point key={point}>{point}</Point>
                ))}
              </ul>
            </article>
          </Reveal>
        </div>
        <Reveal delay={100}>
          <div
            className="mx-auto mt-16 max-w-[860px] rounded-[36px] border border-brand-teal/10 bg-white/60 px-8 py-10 text-center md:px-14"
            data-testid="one-clearer-story"
          >
            <p className="text-[26px] font-extrabold text-brand-teal md:text-[32px]">
              One clearer story.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-3 md:gap-8">
              <p className="text-[17px] font-semibold leading-snug md:text-[18px]">
                The patient experiences the day.
              </p>
              <p className="text-[17px] font-semibold leading-snug md:text-[18px]">
                Sophie helps organize what changed.
              </p>
              <p className="text-[17px] font-semibold leading-snug md:text-[18px]">
                The care team can review the context.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
